import React from "react";
import { Music, Cpu, GraduationCap } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useReveal } from "@/hooks/useReveal";
import { IMAGES } from "@/lib/images";

const PillarCard = ({ idx, icon: Icon, title, desc, tags, image, testid }) => (
  <article
    data-testid={testid}
    className="group relative overflow-hidden border border-white/10 bg-[var(--ps-surface)] hover:border-[var(--ps-gold)]/50 transition-all duration-700"
  >
    <div className="relative h-56 md:h-64 overflow-hidden">
      <img
        src={image}
        alt={title}
        className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 scale-105 group-hover:scale-100 transition-all duration-[1200ms]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[var(--ps-surface)] via-[var(--ps-surface)]/40 to-transparent" />
      <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
        <span className="ps-number text-2xl text-[var(--ps-gold)]">0{idx}</span>
        <Icon className="w-5 h-5 text-[var(--ps-gold)]" strokeWidth={1.25} />
      </div>
    </div>
    <div className="p-7 md:p-8">
      <h3 className="font-serif-display text-3xl md:text-4xl text-white mb-4">{title}</h3>
      <p className="text-sm text-zinc-400 leading-relaxed mb-6">{desc}</p>
      <div className="flex flex-wrap gap-2">
        {tags.map((tag) => (
          <span
            key={tag}
            className="text-[10px] tracking-[0.22em] uppercase border border-white/15 text-zinc-300 px-3 py-1"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  </article>
);

const Pillars = () => {
  const { t } = useLanguage();
  const { ref, visible } = useReveal();

  return (
    <section
      id="services"
      data-testid="pillars-section"
      className="relative py-28 md:py-40 border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-6 h-[1px] bg-[var(--ps-gold)]" />
              <span className="text-xs tracking-[0.3em] text-[var(--ps-gold)]">
                {t.pillars.kicker}
              </span>
            </div>
            <h2
              data-testid="pillars-title"
              className="font-serif-display text-4xl md:text-6xl leading-[1.02] text-white max-w-3xl"
            >
              {t.pillars.title.split(" ").slice(0, 2).join(" ")}{" "}
              <span className="italic text-[var(--ps-gold)]">
                {t.pillars.title.split(" ").slice(2).join(" ")}
              </span>
            </h2>
          </div>
        </div>

        <div
          ref={ref}
          className={`grid md:grid-cols-3 gap-6 md:gap-8 reveal-stagger ${
            visible ? "is-visible" : ""
          }`}
        >
          <PillarCard
            idx={1}
            icon={Music}
            image={IMAGES.pillarMusic}
            testid="pillar-music"
            title={t.pillars.music.name}
            desc={t.pillars.music.desc}
            tags={t.pillars.music.tags}
          />
          <PillarCard
            idx={2}
            icon={Cpu}
            image={IMAGES.pillarIt}
            testid="pillar-it"
            title={t.pillars.it.name}
            desc={t.pillars.it.desc}
            tags={t.pillars.it.tags}
          />
          <PillarCard
            idx={3}
            icon={GraduationCap}
            image={IMAGES.pillarEducation}
            testid="pillar-education"
            title={t.pillars.education.name}
            desc={t.pillars.education.desc}
            tags={t.pillars.education.tags}
          />
        </div>
      </div>
    </section>
  );
};

export default Pillars;
